import type { ReactNode } from 'react'
import { Modal } from './Modal'
import { Button } from './Button'

// ── Types ─────────────────────────────────────────────────────────────────────

export type ConfirmationVariant = 'default' | 'danger'

export interface ConfirmationDialogProps {
  /** Controls visibility. */
  open: boolean
  /** Called when the user dismisses (Escape, backdrop, X, Cancel button). */
  onClose: () => void
  /** Called when the user activates the primary button. */
  onConfirm: () => void
  /** Title text. */
  title: string
  /** Optional subtitle / one-line context. */
  subtitle?: string
  /**
   * Body slot — text or a form. When a form, the consumer is responsible
   * for its own validation; ConfirmationDialog only fires onConfirm.
   *
   * Enter-to-confirm decision: we intentionally do NOT bind Enter globally.
   * When `children` contains a form (input/textarea/select), a global Enter
   * binding would conflict with native form submission or textarea newlines.
   * Keyboard users reach the Confirm button via Tab and activate with Space/Enter
   * natively — this is sufficient and avoids surprising form interactions.
   */
  children?: ReactNode
  /** Primary button label. Defaults to "Confirm". */
  confirmLabel?: string
  /** Secondary button label. Defaults to "Cancel". */
  cancelLabel?: string
  /**
   * Variant — `danger` styles the primary button as destructive (clay accent).
   * Default uses terracotta (primary).
   */
  variant?: ConfirmationVariant
  /**
   * Disable the confirm button (e.g., while a form is invalid).
   * The dialog stays open; consumer handles state.
   */
  confirmDisabled?: boolean
  /** Show a loading state on the primary button. Disables it implicitly. */
  loading?: boolean
  /**
   * Optional override of the entire footer. When provided, the default
   * Cancel + Confirm buttons are not rendered — caller owns the row.
   */
  actions?: ReactNode
  /** data-testid passthrough for tests. */
  'data-testid'?: string
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Reusable confirmation / decision modal.
 *
 * Composes `Modal` for shell infrastructure (portal, backdrop, focus trap,
 * Escape handler, slide-in animation, drag-to-dismiss on mobile).
 * Adds confirmation semantics: structured header (title + optional subtitle),
 * a body slot, and a default Cancel + Confirm footer.
 *
 * For destructive actions, pass `variant="danger"` to style the primary button
 * in clay (--danger). For regular confirmations, the default terracotta is used.
 *
 * When `actions` is provided, the default footer is replaced entirely — use
 * this for non-standard button arrangements (e.g., three-option decisions).
 */
export function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  subtitle,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  confirmDisabled = false,
  loading = false,
  actions,
  'data-testid': testId,
}: ConfirmationDialogProps): JSX.Element | null {
  if (!open) return null

  const defaultFooter = (
    <>
      <Button
        variant="secondary"
        size="md"
        onClick={onClose}
        data-testid={testId != null ? `${testId}-cancel` : undefined}
      >
        {cancelLabel}
      </Button>
      <Button
        variant={variant === 'danger' ? 'destructive' : 'primary'}
        size="md"
        onClick={onConfirm}
        disabled={confirmDisabled}
        loading={loading}
        data-testid={testId != null ? `${testId}-confirm` : undefined}
      >
        {confirmLabel}
      </Button>
    </>
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={actions ?? defaultFooter}
    >
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        data-testid={testId != null ? `${testId}-body` : undefined}
      >
        {subtitle != null && (
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: 'var(--text-2)',
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </p>
        )}
        {children != null && (
          <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6 }}>
            {children}
          </div>
        )}
      </div>
    </Modal>
  )
}
